import { useGet, useMutationWithErrorHandling, usePost, usePut, useQueryWithErrorHandling } from '@app/sdk';
import { useQueryClient } from '@tanstack/react-query';

function useDelinquencyService() {
  const get = useGet(true, 'ams');
  const post = usePost(true, 'ams');
  const put = usePut(true, 'ams');

  function getNosiasForAccount(accountId: string) {
    return get<{ value: GetNosiasForAccount[] }>(
      `/api/proxy/ams/delinquency-service/accounts/${accountId}/notice-of-sums-in-arrears`
    );
  }

  function getPersistentDebtStatusForAccount(accountId: string) {
    return get<PersistentDebtStatusResponse>(`/api/proxy/ams/delinquency-service/accounts/${accountId}/persistent-debt/status`);
  }

  function getPersistentDebtForAccount(accountId: string) {
    return get<PersistentDebtResponse>(`/api/proxy/ams/delinquency-service/accounts/${accountId}/persistent-debt`);
  }

  function getPayDownPlanQuotes(accountId: string): Promise<PayDownPlanQuotesResponse> {
    return get<PayDownPlanQuotesResponse>(`/api/proxy/ams/delinquency-service/accounts/${accountId}/paydown-plan-quotes`);
  }

  function getAccountPayDownPlanQuotesStatus(accountId: string): Promise<PayDownPlanQuotesStatusResponse> {
    return get<PayDownPlanQuotesStatusResponse>(`/api/proxy/ams/delinquency-service/accounts/${accountId}/paydown-plan-quotes/status`);
  }

  function getAccountPayDownEligibleBalances(accountId: string): Promise<PayDownEligibleBalancesResponse> {
    return get<PayDownEligibleBalancesResponse>(`/api/proxy/ams/delinquency-service/accounts/${accountId}/paydown-plan/eligible-balances`);
  }
  
  function requestQuoteOptIn(accountId: string, quoteId: string) {
    return post(
      `/api/proxy/ams/delinquency-service/accounts/${accountId}/paydown-plan-quotes/${quoteId}`,
      {
      }
    );
  }

  function requestAllQuotesOptOut(accountId: string) {
    return put(`/api/proxy/ams/delinquency-service/accounts/${accountId}/paydown-plan-quotes/opt-out`, { });
  }

  function requestQuotesOptIn(accountId: string) {
    return put(`/api/proxy/ams/delinquency-service/accounts/${accountId}/paydown-plan-quotes/opt-in`, { });
  }

  return { 
    getNosiasForAccount,
    getPersistentDebtStatusForAccount,
    getPersistentDebtForAccount, 
    getPayDownPlanQuotes, 
    getAccountPayDownPlanQuotesStatus,
    getAccountPayDownEligibleBalances, 
    requestQuoteOptIn, 
    requestAllQuotesOptOut, 
    requestQuotesOptIn };
}

export function useGetNosiasForAccount(accountId: string) {
  const { getNosiasForAccount } = useDelinquencyService();
  return useQueryWithErrorHandling({
    queryKey: ['nosiasForAccount', accountId],
    queryFn: () => getNosiasForAccount(accountId),
  });
}

export function useRefreshGetNosiasForAccount(accountId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['nosiasForAccount', accountId] });
}

export function useGetPersistentDebtStatusForAccount(accountId: string) {
  const { getPersistentDebtStatusForAccount } = useDelinquencyService();
  return useQueryWithErrorHandling({
    queryKey: ['persistentDebtStatusForAccount', accountId],
    queryFn: () => getPersistentDebtStatusForAccount(accountId),
  });
}

export function useGetPersistentDebtForAccount(accountId: string) {
  const { getPersistentDebtForAccount } = useDelinquencyService();
  return useQueryWithErrorHandling({
    queryKey: ['persistentDebtForAccount', accountId],
    queryFn: () => getPersistentDebtForAccount(accountId),
  });
}

export function useGetPayDownPlanQuotes(accountId: string) {
  const { getPayDownPlanQuotes } = useDelinquencyService();
  return useQueryWithErrorHandling({
    queryKey: ['payDownPlanQuotes', accountId],
    queryFn: () => getPayDownPlanQuotes(accountId),
  });
}

export function useGetAccountPayDownPlanQuotesStatus(accountId: string) {
  const { getAccountPayDownPlanQuotesStatus } = useDelinquencyService();
  return useQueryWithErrorHandling({
    queryKey: ['payDownPlanQuotesStatus', accountId],
    queryFn: () => getAccountPayDownPlanQuotesStatus(accountId),
  });
}

export function useGetAccountPayDownEligibleBalances(accountId: string) {
  const { getAccountPayDownEligibleBalances } = useDelinquencyService();
  return useQueryWithErrorHandling({
    queryKey: ['payDownEligibleBalances', accountId],
    queryFn: () => getAccountPayDownEligibleBalances(accountId),
  });
}
export function useRequestQuoteOptIn() {
  const { requestQuoteOptIn } = useDelinquencyService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId,
      quoteId,
    }: {
      accountId: string;
      quoteId: string;
    }) => requestQuoteOptIn(accountId, quoteId)
  });
}

export function useRequestAllQuotesOptOut() {
  const { requestAllQuotesOptOut } = useDelinquencyService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId
    }: {
      accountId: string;
    }) => requestAllQuotesOptOut(accountId)
  });
}

export function useRequestQuotesOptIn() {
  const { requestQuotesOptIn } = useDelinquencyService();
  return useMutationWithErrorHandling({
    mutationFn: ({
      accountId
    }: {
      accountId: string;
    }) => requestQuotesOptIn(accountId)
  });
}

export function useRefreshQuotesStatus() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['payDownPlanQuotesStatus'] });
}

export function useRefreshQuotes() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['payDownPlanQuotes'] });
}